import { css } from "@emotion/css";
import { fontSize, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { PropertyValueResource, ActionTemplateParameterResource, EnvironmentResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { TemplateNameCell } from "~/areas/projects/components/Variables/TenantVariables/TemplateNameCell";
import type { LoadedSourceItems } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import type { DataTableColumn } from "~/components/GroupedDataTable/GroupedDataTable";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import ReadonlyText from "~/components/ReadonlyText";
import { TenantTemplateScopeCell } from "./ScopedTenantTemplateScopeCell";
import { TenantTemplateValueCell } from "./ScopedTenantTemplateValueCell";
export interface TenantVariableDataGridColumn {
    type: "Template" | "Tenant" | "Value" | "Environments" | "Actions";
    width: string;
}
interface Variable {
    Id: string;
    Name: string;
}
export interface RowData {
    Id: string;
    Template: ActionTemplateParameterResource;
    Tenant?: TenantResource;
    Container: Variable;
    Value: PropertyValueResource;
    Status: "overridden" | "default" | "missing" | "new";
    EnvironmentIds: string[];
    IsTemplateChild: boolean;
    IsTenantChild: boolean;
    HasValueChanged: boolean;
    HasScopeChanged: boolean;
}
export interface TenantVariableDataGridColumn {
    type: "Template" | "Tenant" | "Value" | "Environments" | "Actions";
    width: string;
}
export interface ScopedTenantTemplateDataGridProps {
    data: RowData[];
    enabledColumns: TenantVariableDataGridColumn[];
    groupNames: string[];
    environments: EnvironmentResource[];
    variableSources: LoadedSourceItems;
    hasEditPermissions: boolean;
    doBusyTask: (action: () => Promise<void>) => Promise<boolean>;
    onVariableValueUpdate: (variableId: string, value: PropertyValueResource | undefined) => void;
    onVariableScopeUpdate: (variableId: string, environmentIds: string[]) => void;
    onVariableRemove: (variableId: string) => void;
    onVariableAdd: (tenantId: string | undefined, libraryVariableSetId: string, templateId: string) => void;
}
interface FocusState {
    itemId: string;
    cell: "value" | "scope" | "none";
}
export function ScopedTenantTemplateDataGrid(props: ScopedTenantTemplateDataGridProps): JSX.Element {
    const { data, groupNames, enabledColumns, environments, variableSources, hasEditPermissions, doBusyTask, onVariableValueUpdate, onVariableScopeUpdate, onVariableAdd, onVariableRemove } = props;
    const [focusState, setFocusState] = React.useState<FocusState>({ itemId: "", cell: "none" });
    const columns = enabledColumns.reduce((acc: DataTableColumn<RowData>[], column) => {
        switch (column.type) {
            case "Template":
                return [
                    ...acc,
                    {
                        title: "Name",
                        render: (item) => (item.IsTemplateChild ? null : <TemplateNameCell template={item.Template}/>),
                        isChild: (item) => item.IsTemplateChild,
                        width: column.width,
                    },
                ];
            case "Tenant": {
                return [
                    ...acc,
                    {
                        title: "Tenant",
                        render: (item) => (item.IsTenantChild && item.Tenant ? <ReadonlyText text={item.Tenant.Name} monoSpacedFont/> : null),
                        width: column.width,
                    },
                ];
            }
            case "Value":
                return [
                    ...acc,
                    {
                        title: "Value",
                        render: (item) => {
                            return (<TenantTemplateValueCell item={item} sourceItems={variableSources} localNames={groupNames} doBusyTask={doBusyTask} onChange={(newVal) => {
                                    onVariableValueUpdate(item.Id, newVal);
                                    if (!newVal) {
                                        onVariableScopeUpdate(item.Id, []);
                                    }
                                }} enabled={hasEditPermissions} isFocused={focusState.itemId === item.Id && focusState.cell === "value"} onFocus={() => setFocusState({ itemId: item.Id, cell: "value" })} onBlur={() => setFocusState({ itemId: "", cell: "none" })}/>);
                        },
                        width: column.width,
                        padding: "0",
                        backgroundColor: (item) => (item.HasValueChanged ? themeTokens.color.callout.background.warning.default : undefined),
                    },
                ];
            case "Environments":
                return [
                    ...acc,
                    {
                        title: "Environments",
                        render: (item) => (<TenantTemplateScopeCell item={item} environments={environments} onChange={(newScope) => onVariableScopeUpdate(item.Id, newScope)} enabled={hasEditPermissions && !!item.Value} canBeScoped={!!item.Value} isFocused={focusState.itemId === item.Id && focusState.cell === "scope"} onFocus={() => setFocusState({ itemId: item.Id, cell: "scope" })} onBlur={() => setFocusState({ itemId: "", cell: "none" })}/>),
                        padding: "0",
                        width: column.width,
                        backgroundColor: (item) => (item.HasScopeChanged ? themeTokens.color.callout.background.warning.default : undefined),
                    },
                ];
            case "Actions":
                return [
                    ...acc,
                    {
                        title: "",
                        render: (item) => (<div className={styles.variablesOverflowMenu}>
                                {hasEditPermissions ? (<OverflowMenu menuItems={[OverflowMenuItems.item("Add", () => onVariableAdd(item.Tenant?.Id, item.Container.Id, item.Template.Id)), OverflowMenuItems.item("Delete", () => onVariableRemove(item.Id))]}/>) : null}
                            </div>),
                        width: column.width,
                    },
                ];
            default:
                return acc;
        }
    }, []);
    return (<div>
            <GroupedDataTable data={data} groupBy={data.map((item) => ({
            key: item.Container.Name,
            selector: (item: RowData) => item.Container.Name,
            header: (<tr className={styles.variableSetHeaderContainer}>
                            <td colSpan={5} style={{ padding: `${space[8]} ${space[16]} ${space[8]} ${space[16]}` }}>
                                <div className={styles.variableSetHeader}>{item.Container.Name}</div>
                            </td>
                        </tr>),
        }))} columns={columns} getRowKey={(item) => item.Id} accessibleName="Tenant Variables"/>
        </div>);
}
const styles = {
    variablesOverflowMenu: css({
        display: "flex",
        justifyContent: "flex-end",
    }),
    variableSetHeaderContainer: css({
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    variableSetHeader: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        fontWeight: fontWeight[700],
        fontSize: fontSize["base"],
    }),
};
